import { useRef, useCallback, useState, useMemo } from 'react';
import { ProgressBar } from './ProgressBar';
import { StepRenderer, type StepRendererRef } from './StepRenderer';
import { NavigationButtons } from './NavigationButtons';
import { useMultiStepForm } from '~/hooks/useMultiStepForm';
import { useAutoAdvance } from '~/hooks/useAutoAdvance';
import { useIframeEvents } from '~/hooks/useIframeEvents';
import type { IFormSchema } from '~/types';

interface MultiStepFormProps {
  schema: IFormSchema;
  onSubmit: (formData: Record<string, unknown>) => void;
  onChange?: (formData: Record<string, unknown>, currentStep: number) => void;
  initialData?: Record<string, unknown>;
  initialStep?: number;
}

export const MultiStepForm = ({ schema, onSubmit, onChange, initialData, initialStep = 0 }: MultiStepFormProps) => {
  const stepRendererRef = useRef<StepRendererRef>(null);
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const {
    currentStep,
    currentStepConfig,
    totalSteps,
    formData,
    stepErrors,
    isFirstStep,
    isLastStep,
    goToNextStep,
    goToPreviousStep,
    validateCurrentStep,
    updateFormData,
    getProgress,
  } = useMultiStepForm({
    schema,
    initialData,
    initialStep,
  });

  // Detect if current step is a completion step (no fields)
  const isCompletionStep = useMemo(() => {
    return currentStepConfig?.fields?.length === 0;
  }, [currentStepConfig]);

  // Detect if current step has postMessage auto-advance (e.g., Calendly)
  const hasPostMessageAutoAdvance = useMemo(() => {
    return currentStepConfig?.autoAdvance?.enabled && currentStepConfig?.autoAdvance?.trigger === 'postMessage';
  }, [currentStepConfig]);

  // Find the last "real" step (one with fields) to determine when to submit
  const lastRealStepIndex = useMemo(() => {
    const steps = schema.steps || [];
    for (let i = steps.length - 1; i >= 0; i--) {
      if (steps[i].fields.length > 0) {
        return i;
      }
    }
    return steps.length - 1;
  }, [schema.steps]);

  const isLastRealStep = currentStep === lastRealStepIndex;

  // Animated step transition
  const animateToStep = useCallback(
    (stepFn: () => boolean | void, dir: 'forward' | 'backward') => {
      if (isAnimating) return;
      setDirection(dir);
      setIsAnimating(true);

      setTimeout(() => {
        stepFn();
        setTimeout(() => setIsAnimating(false), 400);
      }, 150);
    },
    [isAnimating],
  );

  // Handle auto-advance from field changes or postMessage
  const handleAutoAdvance = useCallback(() => {
    // For postMessage (Calendly), submit first if this is the last real step
    if (hasPostMessageAutoAdvance && isLastRealStep && !hasSubmitted) {
      setHasSubmitted(true);
      onSubmit(formData);
    }

    if (validateCurrentStep()) {
      setIsAutoAdvancing(true);
      animateToStep(() => {
        goToNextStep();
        setIsAutoAdvancing(false);
      }, 'forward');
    }
  }, [
    validateCurrentStep,
    goToNextStep,
    animateToStep,
    hasPostMessageAutoAdvance,
    isLastRealStep,
    hasSubmitted,
    onSubmit,
    formData,
  ]);

  // Set up auto-advance for field changes
  useAutoAdvance({
    schema,
    currentStepConfig,
    formData,
    onAdvance: handleAutoAdvance,
    enabled: !isAutoAdvancing && !isAnimating && !isCompletionStep,
  });

  // Set up iframe event listening for postMessage auto-advance
  useIframeEvents({
    currentStepConfig,
    onAdvance: handleAutoAdvance,
    onEventReceived: (eventType, data) => {
      if (eventType === 'calendly.event_scheduled' && currentStepConfig) {
        const calendlyField = currentStepConfig.fields.find((f) => {
          const uiWidget = (schema.uiSchema?.[f] as Record<string, unknown>)?.['ui:widget'];
          return uiWidget === 'iframe';
        });
        if (calendlyField) {
          updateFormData({ [calendlyField]: data });
        }
      }
    },
    enabled: !isAutoAdvancing && !isAnimating,
  });

  // Save on blur instead of every keystroke to avoid partial data
  const handleBlur = useCallback(() => {
    onChange?.(formData, currentStep);
  }, [formData, currentStep, onChange]);

  // Handle step field changes (local state only, no save)
  const handleStepChange = useCallback(
    (stepData: Record<string, unknown>) => {
      updateFormData(stepData);
    },
    [updateFormData],
  );

  // Handle continue button click
  const handleContinue = useCallback(() => {
    if (validateCurrentStep()) {
      onChange?.(formData, currentStep);
      animateToStep(goToNextStep, 'forward');
    }
  }, [validateCurrentStep, goToNextStep, animateToStep, onChange, formData, currentStep]);

  // Handle submit button click
  const handleSubmit = useCallback(() => {
    if (validateCurrentStep()) {
      setHasSubmitted(true);
      onSubmit(formData);
      // If there's a completion step after, advance to it
      if (!isLastStep) {
        animateToStep(goToNextStep, 'forward');
      }
    }
  }, [validateCurrentStep, formData, onSubmit, isLastStep, animateToStep, goToNextStep]);

  // Handle back button click
  const handleBack = useCallback(() => {
    onChange?.(formData, currentStep);
    animateToStep(goToPreviousStep, 'backward');
  }, [goToPreviousStep, animateToStep, onChange, formData, currentStep]);

  if (!currentStepConfig) {
    return <div className="text-destructive">Invalid step configuration</div>;
  }

  return (
    <div className="w-full max-w-2xl mx-auto p-6">
      <ProgressBar
        currentStep={currentStep}
        totalSteps={totalSteps}
        stepTitle={currentStepConfig.title}
        stepDescription={currentStepConfig.description}
        progress={getProgress()}
      />

      {/* Animated Step Container */}
      <div className="relative overflow-hidden -mx-2 -my-1">
        <div
          key={currentStep}
          className={`px-2 py-1 transition-all ${
            isAnimating
              ? direction === 'forward'
                ? 'opacity-0 translate-x-20'
                : 'opacity-0 -translate-x-20'
              : 'opacity-100 translate-x-0'
          }`}
          style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)', transitionDuration: '400ms' }}
        >
          {isCompletionStep ? (
            <div className="text-center py-12">
              <h2 className="text-2xl font-semibold text-foreground mb-2">{currentStepConfig.title}</h2>
              {currentStepConfig.description && (
                <p className="text-lg text-muted-foreground">{currentStepConfig.description}</p>
              )}
            </div>
          ) : (
            <StepRenderer
              key={`step-${currentStep}`}
              ref={stepRendererRef}
              schema={schema}
              stepConfig={currentStepConfig}
              formData={formData}
              stepErrors={stepErrors}
              onChange={handleStepChange}
              onBlur={handleBlur}
            />
          )}
        </div>
      </div>

      <NavigationButtons
        isFirstStep={isFirstStep}
        isLastStep={isLastRealStep}
        isAutoAdvancing={isAutoAdvancing}
        hideMainButton={hasPostMessageAutoAdvance}
        isCompletionStep={isCompletionStep}
        onBack={handleBack}
        onContinue={handleContinue}
        onSubmit={handleSubmit}
        disabled={false}
      />
    </div>
  );
};
